import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  FunnelIcon,
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  BellIcon,
  BellSlashIcon,
  UserGroupIcon,
  XMarkIcon,
  ChevronRightIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import {
  getSavedFilters,
  saveFilter,
  deleteSavedFilter,
  getFilterMatches,
  updateSavedFilter
} from '../services/api';

// Common skills for suggestions
const COMMON_SKILLS = [
  'JavaScript', 'Python', 'React', 'Node.js', 'TypeScript',
  'Java', 'C++', 'AWS', 'Docker', 'Kubernetes',
  'SQL', 'MongoDB', 'PostgreSQL', 'Machine Learning', 'Data Science',
  'Product Management', 'UI/UX', 'Agile', 'Scrum', 'DevOps'
];

// Common domains
const DOMAINS = [
  'Software Engineering',
  'Data Science',
  'Product Management',
  'Design',
  'Marketing',
  'Sales',
  'Finance',
  'Operations',
  'Human Resources',
  'Customer Success'
];

function SavedSearches() {
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [matchingCandidates, setMatchingCandidates] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);

  // New filter form state
  const [newFilter, setNewFilter] = useState({
    name: '',
    filters: {
      domain: '',
      skills: [],
      location: '',
      minMatchScore: 0,
      visaStatus: ''
    },
    alertEnabled: true,
    alertFrequency: 'instant'
  });
  const [skillInput, setSkillInput] = useState('');

  useEffect(() => {
    loadFilters();
  }, []);

  const loadFilters = async () => {
    try {
      setLoading(true);
      const response = await getSavedFilters();
      setFilters(response.data.filters || []);
    } catch (error) {
      console.error('Error loading filters:', error);
      toast.error('Failed to load saved searches');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFilter = async () => {
    if (!newFilter.name.trim()) {
      toast.error('Please enter a name for your search');
      return;
    }

    try {
      const response = await saveFilter(newFilter);
      toast.success(response.data.message || 'Search saved successfully!');
      setFilters([response.data.filter, ...filters]);
      setIsModalOpen(false);
      resetNewFilter();
    } catch (error) {
      console.error('Error creating filter:', error);
      toast.error('Failed to save search');
    }
  };

  const handleDeleteFilter = async (filterId) => {
    if (!window.confirm('Are you sure you want to delete this saved search?')) {
      return;
    }

    try {
      await deleteSavedFilter(filterId);
      setFilters(filters.filter(f => f._id !== filterId));
      toast.success('Search deleted');
      if (selectedFilter?._id === filterId) {
        setSelectedFilter(null);
        setMatchingCandidates([]);
      }
    } catch (error) {
      console.error('Error deleting filter:', error);
      toast.error('Failed to delete search');
    }
  };

  const handleToggleAlerts = async (filter) => {
    try {
      const updatedFilter = {
        ...filter,
        alertEnabled: !filter.alertEnabled
      };
      await updateSavedFilter(filter._id, { alertEnabled: !filter.alertEnabled });
      setFilters(filters.map(f =>
        f._id === filter._id ? { ...f, alertEnabled: !f.alertEnabled } : f
      ));
      toast.success(updatedFilter.alertEnabled ? 'Alerts enabled' : 'Alerts disabled');
    } catch (error) {
      console.error('Error toggling alerts:', error);
      toast.error('Failed to update alerts');
    }
  };

  const handleViewMatches = async (filter) => {
    setSelectedFilter(filter);
    setLoadingMatches(true);
    try {
      const response = await getFilterMatches(filter._id);
      setMatchingCandidates(response.data.candidates || []);
    } catch (error) {
      console.error('Error loading matches:', error);
      toast.error('Failed to load matching candidates');
      setMatchingCandidates([]);
    } finally {
      setLoadingMatches(false);
    }
  };

  const addSkill = (skill) => {
    if (skill && !newFilter.filters.skills.includes(skill)) {
      setNewFilter({
        ...newFilter,
        filters: {
          ...newFilter.filters,
          skills: [...newFilter.filters.skills, skill]
        }
      });
    }
    setSkillInput('');
  };

  const removeSkill = (skillToRemove) => {
    setNewFilter({
      ...newFilter,
      filters: {
        ...newFilter.filters,
        skills: newFilter.filters.skills.filter(s => s !== skillToRemove)
      }
    });
  };

  const resetNewFilter = () => {
    setNewFilter({
      name: '',
      filters: {
        domain: '',
        skills: [],
        location: '',
        minMatchScore: 0,
        visaStatus: ''
      },
      alertEnabled: true,
      alertFrequency: 'instant'
    });
    setSkillInput('');
    setIsCreateMode(false);
  };

  const openCreateModal = () => {
    resetNewFilter();
    setIsCreateMode(true);
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Saved Searches</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create and manage saved search filters with smart match alerts
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          New Search
        </button>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Saved Filters List */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Your Saved Searches</h3>
            </div>
            <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500">Loading...</p>
                </div>
              ) : filters.length === 0 ? (
                <div className="p-8 text-center">
                  <FunnelIcon className="h-12 w-12 text-gray-300 mx-auto" />
                  <p className="mt-2 text-sm text-gray-500">No saved searches yet</p>
                  <button
                    onClick={openCreateModal}
                    className="mt-4 text-indigo-600 hover:text-indigo-500 text-sm font-medium"
                  >
                    Create your first search
                  </button>
                </div>
              ) : (
                filters.map((filter) => (
                  <div
                    key={filter._id}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedFilter?._id === filter._id ? 'bg-indigo-50 border-l-4 border-indigo-600' : ''
                    }`}
                    onClick={() => handleViewMatches(filter)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-medium text-gray-900 truncate">
                            {filter.name}
                          </h4>
                          {filter.alertEnabled ? (
                            <BellIcon className="h-4 w-4 text-green-500" />
                          ) : (
                            <BellSlashIcon className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {filter.filters?.domain && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {filter.filters.domain}
                            </span>
                          )}
                          {filter.filters?.location && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              {filter.filters.location}
                            </span>
                          )}
                          {filter.filters?.skills?.slice(0, 2).map((skill) => (
                            <span
                              key={skill}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800"
                            >
                              {skill}
                            </span>
                          ))}
                          {filter.filters?.skills?.length > 2 && (
                            <span className="text-xs text-gray-500">
                              +{filter.filters.skills.length - 2} more
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          {filter.currentMatchCount || filter.matchCount || 0} matches
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleAlerts(filter);
                          }}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title={filter.alertEnabled ? 'Disable alerts' : 'Enable alerts'}
                        >
                          {filter.alertEnabled ? (
                            <BellIcon className="h-4 w-4" />
                          ) : (
                            <BellSlashIcon className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFilter(filter._id);
                          }}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Delete search"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                        <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Matching Candidates */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedFilter ? (
                  <>
                    Matches for "{selectedFilter.name}"
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      ({matchingCandidates.length} candidates)
                    </span>
                  </>
                ) : (
                  'Select a search to view matches'
                )}
              </h3>
            </div>
            <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
              {!selectedFilter ? (
                <div className="p-8 text-center">
                  <MagnifyingGlassIcon className="h-12 w-12 text-gray-300 mx-auto" />
                  <p className="mt-2 text-sm text-gray-500">
                    Select a saved search to view matching candidates
                  </p>
                </div>
              ) : loadingMatches ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500">Finding matches...</p>
                </div>
              ) : matchingCandidates.length === 0 ? (
                <div className="p-8 text-center">
                  <UserGroupIcon className="h-12 w-12 text-gray-300 mx-auto" />
                  <p className="mt-2 text-sm text-gray-500">No matching candidates found</p>
                  <p className="mt-1 text-xs text-gray-400">
                    Try adjusting your search criteria
                  </p>
                </div>
              ) : (
                matchingCandidates.map((candidate) => (
                  <div key={candidate._id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-medium text-gray-900">
                            {candidate.fullName}
                          </h4>
                          {candidate.matchScore >= 80 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              {candidate.matchScore}% match
                            </span>
                          )}
                          {candidate.matchScore >= 50 && candidate.matchScore < 80 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              {candidate.matchScore}% match
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{candidate.email}</p>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {candidate.domain && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {candidate.domain}
                            </span>
                          )}
                          {candidate.location && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              {candidate.location}
                            </span>
                          )}
                          {candidate.skills?.slice(0, 4).map((skill) => (
                            <span
                              key={skill}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                            >
                              {skill}
                            </span>
                          ))}
                          {candidate.skills?.length > 4 && (
                            <span className="text-xs text-gray-500">
                              +{candidate.skills.length - 4} more
                            </span>
                          )}
                        </div>
                        {candidate.pipelineStage && (
                          <div className="mt-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              candidate.pipelineStage === 'new' ? 'bg-gray-100 text-gray-800' :
                              candidate.pipelineStage === 'contacted' ? 'bg-blue-100 text-blue-800' :
                              candidate.pipelineStage === 'interviewing' ? 'bg-purple-100 text-purple-800' :
                              candidate.pipelineStage === 'offer' ? 'bg-yellow-100 text-yellow-800' :
                              candidate.pipelineStage === 'hired' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              Stage: {candidate.pipelineStage}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="text-right text-xs text-gray-500">
                        {new Date(candidate.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Filter Modal */}
      <Transition.Root show={isModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={setIsModalOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="absolute right-0 top-0 pr-4 pt-4">
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                      onClick={() => setIsModalOpen(false)}
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                        {isCreateMode ? 'Create New Search' : 'Edit Search'}
                      </Dialog.Title>

                      <div className="mt-6 space-y-4">
                        {/* Search Name */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Search Name *
                          </label>
                          <input
                            type="text"
                            value={newFilter.name}
                            onChange={(e) => setNewFilter({ ...newFilter, name: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="e.g., Senior React Developers in NYC"
                          />
                        </div>

                        {/* Domain */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Domain
                          </label>
                          <select
                            value={newFilter.filters.domain}
                            onChange={(e) => setNewFilter({
                              ...newFilter,
                              filters: { ...newFilter.filters, domain: e.target.value }
                            })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          >
                            <option value="">Any domain</option>
                            {DOMAINS.map((domain) => (
                              <option key={domain} value={domain}>{domain}</option>
                            ))}
                          </select>
                        </div>

                        {/* Skills */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Required Skills
                          </label>
                          <div className="mt-1 flex gap-2">
                            <input
                              type="text"
                              value={skillInput}
                              onChange={(e) => setSkillInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  addSkill(skillInput.trim());
                                }
                              }}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              placeholder="Type a skill and press Enter"
                            />
                            <button
                              type="button"
                              onClick={() => addSkill(skillInput.trim())}
                              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            >
                              Add
                            </button>
                          </div>
                          {/* Skill suggestions */}
                          <div className="mt-2 flex flex-wrap gap-1">
                            {COMMON_SKILLS.filter(s => !newFilter.filters.skills.includes(s))
                              .slice(0, 8)
                              .map((skill) => (
                                <button
                                  key={skill}
                                  type="button"
                                  onClick={() => addSkill(skill)}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
                                >
                                  + {skill}
                                </button>
                              ))}
                          </div>
                          {/* Selected skills */}
                          {newFilter.filters.skills.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {newFilter.filters.skills.map((skill) => (
                                <span
                                  key={skill}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                                >
                                  {skill}
                                  <button
                                    type="button"
                                    onClick={() => removeSkill(skill)}
                                    className="ml-1 text-indigo-600 hover:text-indigo-800"
                                  >
                                    <XMarkIcon className="h-3 w-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Location */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Location
                          </label>
                          <input
                            type="text"
                            value={newFilter.filters.location}
                            onChange={(e) => setNewFilter({
                              ...newFilter,
                              filters: { ...newFilter.filters, location: e.target.value }
                            })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="e.g., New York, Remote, San Francisco"
                          />
                        </div>

                        {/* Minimum Match Score */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Minimum Match Score: {newFilter.filters.minMatchScore}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={newFilter.filters.minMatchScore}
                            onChange={(e) => setNewFilter({
                              ...newFilter,
                              filters: { ...newFilter.filters, minMatchScore: parseInt(e.target.value) }
                            })}
                            className="mt-1 block w-full"
                          />
                          <div className="flex justify-between text-xs text-gray-500">
                            <span>0%</span>
                            <span>50%</span>
                            <span>100%</span>
                          </div>
                        </div>

                        {/* Alert Settings */}
                        <div className="border-t border-gray-200 pt-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <label className="text-sm font-medium text-gray-700">
                                Enable Alerts
                              </label>
                              <p className="text-xs text-gray-500">
                                Get notified when new candidates match this search
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setNewFilter({ ...newFilter, alertEnabled: !newFilter.alertEnabled })}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                                newFilter.alertEnabled ? 'bg-indigo-600' : 'bg-gray-200'
                              }`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  newFilter.alertEnabled ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          </div>

                          {newFilter.alertEnabled && (
                            <div className="mt-3">
                              <label className="block text-sm font-medium text-gray-700">
                                Alert Frequency
                              </label>
                              <select
                                value={newFilter.alertFrequency}
                                onChange={(e) => setNewFilter({ ...newFilter, alertFrequency: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              >
                                <option value="instant">Instant</option>
                                <option value="daily">Daily Digest</option>
                                <option value="weekly">Weekly Summary</option>
                              </select>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 sm:flex sm:flex-row-reverse gap-3">
                    <button
                      type="button"
                      onClick={handleCreateFilter}
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:w-auto"
                    >
                      {isCreateMode ? 'Create Search' : 'Save Changes'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}

export default SavedSearches;
